import { useEffect, useState } from 'react'
import { snakeToTitle } from '../../utils'
import TextInput from './TextInput'
import './SchemaFields.css'
import SwitchField from './SwitchField'
import RadioInput from './RadioInput'
import { SingleSelect } from './SingleSelect'

interface Schema {
    type: 'string' | 'number' | 'boolean' | 'object'
    enum?: string[]
    default?: string
    dropdown?: boolean
    title?: string
    description?: string
    format?: string
    properties?: Record<string, Schema>
    required?: string[]
    minLength?: number
    nullable?: boolean
    pattern?: string
    errorMessage?: string | Record<string, string>
    fileUpload?: boolean
    fileUploadKey?: string
}

interface SchemaProps {
    title?: string
    description?: string
    parent: string
    schema: Schema
    form: any
}

interface NullableSectionProps {
    title: string
    description?: string
    parent: string
    schema: Schema
    form: any
}

function NullableSection({ title, description, parent, schema, form }: NullableSectionProps) {
    const initialValue = form.getValues(parent)
    const hasInitialValue = initialValue != null
        && typeof initialValue === 'object'
        && Object.keys(initialValue).some(k => initialValue[k] != null && initialValue[k] !== '')
    const [enabled, setEnabled] = useState(hasInitialValue)

    const handleToggle = (checked: boolean) => {
        setEnabled(checked)
        if (!checked) {
            form.unregister(parent)
            form.setValue(parent, null)
        }
    }

    return (
        <div className="ui-schema-form ui-schema-nullable">
            <label className="ui-schema-nullable-header">
                <div className="ui-schema-nullable-header-text">
                    <h5>{snakeToTitle(title)}</h5>
                    {description && <p>{description}</p>}
                </div>
                <div className="switch">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(event) => handleToggle(event.target.checked)}
                    />
                    <div className="slider round"></div>
                </div>
            </label>
            {enabled && <SchemaFields
                form={form}
                parent={parent}
                schema={schema}
            />}
        </div>
    )
}

function DropdownField({ form, name, item, required }: { form: any, name: string, item: Schema, required?: boolean }) {
    useEffect(() => {
        if (form.getValues(name) === undefined && item.default !== undefined) form.setValue(name, item.default)
    }, [form, name, item.default])

    return <SingleSelect.Field
        form={form}
        name={name}
        label={item.title}
        subtitle={item.description}
        required={required}
        options={item.enum ?? []}
        getOptionDisplay={snakeToTitle}
    />
}

const SENSITIVE_FIELD_PATTERN = /(?:^|_)(secret|token|password|pass|credential|credentials|key)s?$/

const toSnakeCase = (name: string) => name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()

const isSensitiveField = (key: string, item: Schema) => {
    if (item.format === 'password') return true
    return SENSITIVE_FIELD_PATTERN.test(toSnakeCase(key))
}

export default function SchemaFields({ title, description, parent, form, schema }: SchemaProps) {
    if (!schema?.properties) {
        return <></>
    }

    const props = schema.properties
    const keys = Object.keys(schema.properties)
    return <div className="ui-schema-form">
        {title && <h5>{snakeToTitle(title)}</h5>}
        {description && <p>{description}</p> }
        <div className="ui-schema-fields">
            {keys.map(key => {
                const item = props[key]
                const required = schema.required?.includes(key)
                const title = item.title ?? snakeToTitle(key)
                if (item.enum && item.dropdown) {
                    return <DropdownField key={key} form={form} name={`${parent}.${key}`} item={{ ...item, title }} required={required} />
                } else if (item.enum) {
                    return <RadioInput.Field
                        key={key}
                        form={form}
                        name={`${parent}.${key}`}
                        label={title}
                        subtitle={item.description}
                        required={required}
                        options={item.enum.map((value: string) => ({ key: value, label: snakeToTitle(value) }))}
                    />
                } else if (item.type === 'string' || item.type === 'number') {
                    const sensitive = item.type === 'string' && isSensitiveField(key, item)
                    const inputType = item.type === 'number'
                        ? 'number'
                        : sensitive ? 'password' : 'text'
                    const messages = typeof item.errorMessage === 'object' ? item.errorMessage : undefined
                    const patternMessage = typeof item.errorMessage === 'string'
                        ? item.errorMessage
                        : messages?.pattern
                    return <TextInput.Field
                        key={key}
                        form={form}
                        name={`${parent}.${key}`}
                        type={inputType}
                        label={title}
                        subtitle={item.description}
                        required={required}
                        textarea={(item.minLength ?? 0) >= 80}
                        minLength={item.minLength}
                        pattern={item.pattern ? new RegExp(item.pattern) : undefined}
                        patternMessage={patternMessage}
                        minLengthMessage={messages?.minLength}
                        fileUpload={item.fileUpload}
                        fileUploadKey={item.fileUploadKey}
                    />
                } else if (item.type === 'boolean') {
                    return <SwitchField
                        key={key}
                        form={form}
                        name={`${parent}.${key}`}
                        label={title}
                        subtitle={item.description}
                        required={required}
                    />
                } else if (item.type === 'object') {
                    if (item.nullable) {
                        return <NullableSection
                            key={key}
                            form={form}
                            title={title}
                            description={item.description}
                            parent={`${parent}.${key}`}
                            schema={item}
                        />
                    }
                    return <SchemaFields
                        key={key}
                        form={form}
                        title={title}
                        description={item.description}
                        parent={`${parent}.${key}`}
                        schema={item}
                    />
                }
                return 'no key'
            })}
        </div>
    </div>
}
